import {
  APP_BOOTSTRAP_LISTENER,
  ApplicationRef,
  Injectable,
  Provider,
  inject,
  signal,
} from '@angular/core';
import { AuthHttpInterceptor, AuthService } from '@auth0/auth0-angular';
import { HTTP_AUTH_INTERCEPTOR } from '@ups/xplat/core';
import { Observable, of, throwError } from 'rxjs';
import {
  AUTH0_CALLBACK_SEARCH,
  Auth0CallbackError,
  callbackErrorFailure,
  readAuth0CallbackError,
} from './auth0-callback-error';
import { mountAuth0CallbackError } from './auth0-callback-error.mount';
import { CallbackErrorAwareAuthHttpInterceptor } from './callback-error-aware-auth-http.interceptor';

type RedirectLoginOptions = Parameters<AuthService['loginWithRedirect']>[0];
/**
 * The base getAccessTokenSilently is overloaded (Observable<string>, or the verbose response with
 * detailedResponse: true); one override signature can only cover both with `any`.
 */
// pre-commit-allow: overloaded base signature needs `any` to cover both overloads, Bug 37976
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SilentTokenResult = Observable<any>;

/**
 * Bug 37976: auth0-angular's AuthService that stops the refused-login loop.
 *
 * When Auth0 refuses a login (`?error=access_denied&state=...`), auth0-angular sends the app to `/`, the AuthGuard
 * finds no session and calls `loginWithRedirect`, and Auth0, still holding the refused session, refuses again at once:
 * about one /authorize a second behind a spinner. This service holds every `loginWithRedirect` and silent token
 * request while the callback error stands, so the app makes no further auth attempt. The user leaves it only through `retry` (the Auth0 login
 * screen, `prompt=login`) or `signOut` (ends the refused session). It inherits auth0-angular's constructor and DI.
 */
@Injectable()
export class CallbackErrorAwareAuthService extends AuthService {
  readonly callbackError = signal<Auth0CallbackError | null>(
    readAuth0CallbackError(inject(AUTH0_CALLBACK_SEARCH))
  );

  override loginWithRedirect(options?: RedirectLoginOptions): Observable<void> {
    if (this.callbackError()) return of(undefined);
    return super.loginWithRedirect(options);
  }

  /**
   * A silent token request runs a hidden /authorize (prompt=none) that the refused session answers access_denied
   * again, so while the error stands it fails at once with that error (auth0-spa-js's `error` / `error_description`
   * shape) instead of asking Auth0.
   */
  override getAccessTokenSilently(
    ...args: Parameters<AuthService['getAccessTokenSilently']>
  ): SilentTokenResult {
    const held = this.callbackError();
    if (held) return throwError(() => callbackErrorFailure(held));
    return super.getAccessTokenSilently(...args);
  }

  /**
   * Try again: one redirect to the Auth0 login screen (a silent retry would be refused straight away). The hold stays,
   * so no other caller can redirect while the page leaves; a refused retry comes back as a new callback error.
   */
  retry(): Observable<void> {
    return super.loginWithRedirect({ prompt: 'login' });
  }

  /** Sign out: end the refused Auth0 session and come back to this app. */
  signOut(): void {
    super.logout({ returnTo: globalThis.location?.origin });
  }
}

/**
 * Bug 37976: every Auth0 caller (AuthGuard, the error handler, the layout, the login services) injects AuthService,
 * so the callback-aware service replaces that token, and HTTP_AUTH_INTERCEPTOR gets the matching wrapper; after
 * bootstrap it shows the error panel when one is held.
 */
export function provideAuth0CallbackErrorGuard(): Provider[] {
  return [
    CallbackErrorAwareAuthService,
    { provide: AuthService, useExisting: CallbackErrorAwareAuthService },
    // auth0-angular's interceptor gets tokens from the Auth0 client directly, so it is wrapped too. MyHttpClientFactory
    // adds HTTP_AUTH_INTERCEPTOR only to auth=true clients.
    AuthHttpInterceptor,
    {
      provide: HTTP_AUTH_INTERCEPTOR,
      useClass: CallbackErrorAwareAuthHttpInterceptor,
    },
    {
      provide: APP_BOOTSTRAP_LISTENER,
      multi: true,
      useFactory: () => {
        const appRef = inject(ApplicationRef);
        const auth = inject(CallbackErrorAwareAuthService);
        return () => mountAuth0CallbackError(appRef, auth);
      },
    },
  ];
}
